import { Bounds, CarouselLayout, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';

export interface BoxesLayout {
  boxArea: (index: number, inPreviousLens: boolean) => Bounds;
}

export interface BoxLayoutOptions {
  maxBoxHeight?: number;
}

export function equalInfoBoxesLayoutForSide(
  baseLayout: CarouselLayout,
  side: Side,
  boxCount: number,
  lensIndex: number,
  previousLensIndex: number | undefined,
  options: BoxLayoutOptions,
): BoxesLayout {
  function getVerticalSpacing() {
    return 10;
  }

  const maxBoxHeight = options.maxBoxHeight !== undefined ? options.maxBoxHeight : 130;

  function getTotalVerticalSpacingBetweenBoxes(numberOfBoxes: number): number {
    return getVerticalSpacing() * (numberOfBoxes - 1);
  }

  function getBoxHeight(numberOfBoxes: number) {
    const ba = baseLayout.boxesArea(side); // does not matter which side
    const maxSpaceAvailable = ba.height - (numberOfBoxes - 1) * getVerticalSpacing();
    return Math.min(maxSpaceAvailable / numberOfBoxes, maxBoxHeight);
  }

  function getHeightOfAllBoxes(numberOfBoxes: number) {
    return getBoxHeight(numberOfBoxes) * numberOfBoxes + getTotalVerticalSpacingBetweenBoxes(numberOfBoxes);
  }

  function boxArea(index: number, inPreviousLens: boolean) {
    const ba = baseLayout.boxesArea(side);
    const start = ba.y1 + (ba.height - getHeightOfAllBoxes(boxCount)) / 2;
    const boxHeight = getBoxHeight(boxCount);

    const y1 = start + index * getBoxHeight(boxCount) + getVerticalSpacing() * index;
    const leftPadding = 40;
    const boxWidth = ba.width - leftPadding;

    function getAnimationStartX() {
      if (previousLensIndex === undefined) {
        return undefined;
      }
      if (lensIndex < previousLensIndex) {
        return -boxWidth / 2;
      }
      return ba.x2 - boxWidth / 2;
    }

    function getX1() {
      // if this is a previous one, we wish to push this to the side
      if (inPreviousLens) {
        if (previousLensIndex !== undefined && lensIndex > previousLensIndex) {
          return -boxWidth / 2;
        }
        return ba.x2 + boxWidth * 1.25;
      }
      if (side === Side.RIGHT) {
        return ba.x1 + leftPadding;
      } else {
        return ba.x1;
      }
    }

    const x1 = getX1();
    return {
      animationStartX: getAnimationStartX(),
      ...toBounds({
        y1,
        y2: y1 + boxHeight,
        x1,
        x2: x1 + boxWidth,
      }),
    };
  }

  return { boxArea };
}
